import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  CButton,
  CButtonGroup,
  CForm,
  CFormInput,
  CFormLabel,
  CFormSelect,
  COffcanvas,
  COffcanvasBody,
  COffcanvasHeader,
  COffcanvasTitle,
  CRow,
  CCol,
  CCallout,
  CBadge,
  CInputGroup,
} from "@coreui/react";
import CIcon from "@coreui/icons-react";
import {
  cilCalendar,
  cilCheck,
  cilListFilter,
  cilReload,
  cilVerticalAlignTop,
  cilX,
} from "@coreui/icons";

export default function FilterPanel({ project, onApply }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [open, setOpen] = useState(false);
  const [appliedFrom, setAppliedFrom] = useState("");
  const [appliedTo, setAppliedTo] = useState("");
  const [draftFrom, setDraftFrom] = useState("");
  const [draftTo, setDraftTo] = useState("");
  const [templateYear, setTemplateYear] = useState("");
  const [lastMonths, setLastMonths] = useState(6);
  const [nextMonths, setNextMonths] = useState(3);

  // limites práticos para inputs (se existirem datas do projeto)
  const minMonth = useMemo(
    () => convertToMonthYear(project?.start_date) || undefined,
    [project],
  );
  const maxMonth = useMemo(
    () => convertToMonthYear(project?.end_date) || undefined,
    [project],
  );

  useEffect(() => {
    const defaultFrom = addOneMonth(project?.executed) || "";
    const urlFrom = searchParams.get("filter_from") || "";
    const urlTo = searchParams.get("filter_to") || "";
    const from = urlFrom || defaultFrom;
    const to = urlTo || "";
    setAppliedFrom(from);
    setAppliedTo(to);
    setDraftFrom(from);
    setDraftTo(to);
    setTemplateYear(
      from?.slice(0, 4) ||
        convertToMonthYear(project?.start_date)?.slice(0, 4) ||
        "",
    );
    onApply?.({ from: from || null, to: to || null });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project]); // onApply é estável (setRange); searchParams lidos sem dep para evitar loop

  const invalid = useMemo(
    () => draftFrom && draftTo && draftFrom > draftTo,
    [draftFrom, draftTo],
  );
  const availableYears = useMemo(() => {
    const startY = Number((minMonth || "").slice(0, 4));
    const endY = Number((maxMonth || "").slice(0, 4));
    if (!Number.isFinite(startY) || !Number.isFinite(endY) || startY > endY) {
      return [];
    }
    const out = [];
    for (let y = startY; y <= endY; y++) out.push(String(y));
    return out;
  }, [minMonth, maxMonth]);

  const handleOpen = () => {
    setDraftFrom(appliedFrom || "");
    setDraftTo(appliedTo || "");
    setOpen(true);
  };

  const handleReset = () => {
    const start = project?.executed
      ? addOneMonth(project.executed)
      : convertToMonthYear(project?.start_date);
    const end = convertToMonthYear(project?.end_date);
    setDraftFrom(start || "");
    setDraftTo(end || "");
  };

  const handleFullProject = () => {
    setDraftFrom(convertToMonthYear(project?.start_date) || "");
    setDraftTo(convertToMonthYear(project?.end_date) || "");
  };

  const handleTemplateYear = () => {
    if (!templateYear) return;
    const from = clampMonth(`${templateYear}-01`, minMonth, maxMonth);
    const to = clampMonth(`${templateYear}-12`, minMonth, maxMonth);
    setDraftFrom(from || "");
    setDraftTo(to || "");
  };

  const handleLastMonths = () => {
    const months = Math.max(1, Math.min(60, Number(lastMonths) || 1));
    const anchor = clampMonth(
      appliedTo || maxMonth || toMonthInput(new Date()),
      minMonth,
      maxMonth,
    );
    if (!anchor) return;
    const from = clampMonth(
      subtractMonths(anchor, months - 1),
      minMonth,
      maxMonth,
    );
    setDraftFrom(from || "");
    setDraftTo(anchor);
  };

  const handleApply = () => {
    if (invalid) return;
    setAppliedFrom(draftFrom || "");
    setAppliedTo(draftTo || "");
    onApply?.({ from: draftFrom || null, to: draftTo || null });
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (draftFrom) next.set("filter_from", draftFrom);
        else next.delete("filter_from");
        if (draftTo) next.set("filter_to", draftTo);
        else next.delete("filter_to");
        return next;
      },
      { replace: true },
    );
    setOpen(false);
  };

  const handleCancel = () => {
    setDraftFrom(appliedFrom || "");
    setDraftTo(appliedTo || "");
    setOpen(false);
  };

  const handleNextMonths = () => {
    const months = Math.max(1, Math.min(60, Number(nextMonths) || 1));
    const anchor = clampMonth(
      appliedFrom || minMonth || toMonthInput(new Date()),
      minMonth,
      maxMonth,
    );
    if (!anchor) return;
    const to = clampMonth(addMonths(anchor, months - 1), minMonth, maxMonth);
    setDraftFrom(anchor);
    setDraftTo(to || "");
  };

  const hasActive = Boolean(appliedFrom || appliedTo);
  const appliedActive = (appliedFrom ? 1 : 0) + (appliedTo ? 1 : 0);

  return (
    <>
      {/* Trigger para abrir o painel */}
      <CButton
        color={hasActive ? "primary" : "secondary"}
        variant="outline"
        className="d-flex align-items-center px-2"
        onClick={handleOpen}
        title={`Filtro: ${appliedFrom || ""} → ${appliedTo || ""}`}
      >
        <CIcon icon={cilListFilter} className="me-2" size="lg" />
        Filtrar
        {hasActive && (
          <CBadge color="primary" shape="rounded-pill" className="ms-2">
            {appliedActive}
          </CBadge>
        )}
      </CButton>

      <COffcanvas placement="end" visible={open} onHide={handleCancel}>
        <COffcanvasHeader className="border-bottom">
          <COffcanvasTitle className="me-auto">
            <div className="d-flex align-items-center gap-2">
              <CIcon icon={cilCalendar} />
              Filtro de datas
            </div>
          </COffcanvasTitle>
          <div className="d-flex align-items-center gap-2">
            <CButton size="sm" color="secondary" onClick={handleCancel}>
              Cancelar
            </CButton>
            <CButton
              size="sm"
              color="primary"
              disabled={invalid}
              onClick={handleApply}
            >
              Aplicar
            </CButton>
          </div>
        </COffcanvasHeader>
        <COffcanvasBody>
          <CForm>
            <CRow className="g-3">
              <CCol xs={12}>
                <small className="text-muted">Intervalo</small>
              </CCol>

              <CCol xs={12} md={6}>
                <CFormLabel className="mb-1">Desde</CFormLabel>
                <CFormInput
                  type="month"
                  value={draftFrom}
                  min={minMonth}
                  max={maxMonth}
                  onChange={(e) => setDraftFrom(e.target.value)}
                />
              </CCol>
              <CCol xs={12} md={6}>
                <CFormLabel className="mb-1">Até</CFormLabel>
                <CFormInput
                  type="month"
                  value={draftTo}
                  min={minMonth}
                  max={maxMonth}
                  onChange={(e) => setDraftTo(e.target.value)}
                />
              </CCol>

              <CCol xs={12}>
                <small className="text-muted">Atalhos rápidos</small>
              </CCol>
              <CCol xs={12}>
                <CButtonGroup size="sm">
                  <CButton
                    color="secondary"
                    variant="outline"
                    title="Todo o projeto"
                    onClick={handleFullProject}
                  >
                    <CIcon icon={cilListFilter} className="me-1" />
                    Todo o projeto
                  </CButton>
                  <CButton
                    color="secondary"
                    variant="outline"
                    title="Em execução"
                    onClick={handleReset}
                  >
                    <CIcon icon={cilCalendar} className="me-1" />
                    Em execução
                  </CButton>
                </CButtonGroup>
              </CCol>
              <CCol md={12}>
                <CFormLabel className="mb-1">Ano</CFormLabel>
                <CInputGroup>
                  <CFormSelect
                    value={templateYear}
                    onChange={(e) => setTemplateYear(e.target.value)}
                  >
                    <option value="">Selecionar ano</option>
                    {availableYears.map((year) => (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    ))}
                  </CFormSelect>
                  <CButton
                    size="sm"
                    color="secondary"
                    variant="outline"
                    onClick={handleTemplateYear}
                  >
                    <CIcon icon={cilVerticalAlignTop} />
                  </CButton>
                </CInputGroup>
              </CCol>
              <CCol md={12}>
                <CFormLabel className="mb-1">Últimos meses</CFormLabel>
                <CInputGroup>
                  <CFormInput
                    type="number"
                    min={1}
                    max={60}
                    value={lastMonths}
                    onChange={(e) => setLastMonths(e.target.value)}
                  />
                  <CButton
                    size="sm"
                    color="secondary"
                    variant="outline"
                    onClick={handleLastMonths}
                  >
                    <CIcon icon={cilVerticalAlignTop} />
                  </CButton>
                </CInputGroup>
              </CCol>

              <CCol md={12}>
                <CFormLabel className="mb-1">Próximos meses</CFormLabel>
                <CInputGroup>
                  <CFormInput
                    type="number"
                    min={1}
                    max={60}
                    value={nextMonths}
                    onChange={(e) => setNextMonths(e.target.value)}
                  />
                  <CButton
                    size="sm"
                    color="secondary"
                    variant="outline"
                    onClick={handleNextMonths}
                  >
                    <CIcon icon={cilVerticalAlignTop} />
                  </CButton>
                </CInputGroup>
              </CCol>

              {invalid && (
                <CCol xs={12}>
                  <CCallout color="danger" className="py-2">
                    "Desde" não pode ser posterior a "Até".
                  </CCallout>
                </CCol>
              )}
            </CRow>
          </CForm>

          {/* Resumo atual */}
          <div className="mt-4">
            <small className="text-muted">Aplicado</small>
            <div className="fw-semibold">
              {appliedFrom || "(início)"} → {appliedTo || "(fim)"}
            </div>
          </div>
        </COffcanvasBody>
      </COffcanvas>
    </>
  );
}

// === helpers ===
function addOneMonth(ym) {
  if (!ym) return null;
  const m = /^(\d{4})-(\d{2})$/.exec(ym);
  if (!m) throw new Error("Formato inválido. Usa 'YYYY-MM'.");
  let year = Number(m[1]);
  let month = Number(m[2]); // 1..12
  if (month < 1 || month > 12) throw new Error("Mês fora de 1..12.");
  month += 1;
  if (month === 13) {
    month = 1;
    year += 1;
  }
  return `${year.toString().padStart(4, "0")}-${month
    .toString()
    .padStart(2, "0")}`;
}

function convertToMonthYear(date) {
  return date?.slice(0, 7) || null;
}

function toMonthInput(dateObj) {
  const y = dateObj.getFullYear();
  const m = String(dateObj.getMonth() + 1).padStart(2, "0");
  return `${y}-${m}`;
}

function subtractMonths(monthStr, months) {
  const [y, m] = monthStr.split("-").map(Number);
  const d = new Date(y, m - 1, 1);
  d.setMonth(d.getMonth() - months);
  return toMonthInput(d);
}

function addMonths(monthStr, months) {
  const [y, m] = monthStr.split("-").map(Number);
  const d = new Date(y, m - 1, 1);
  d.setMonth(d.getMonth() + months);
  return toMonthInput(d);
}

function clampMonth(value, minMonth, maxMonth) {
  if (!value) return value;
  let out = value;
  if (minMonth && out < minMonth) out = minMonth;
  if (maxMonth && out > maxMonth) out = maxMonth;
  return out;
}
